import React from 'react'
import isBrowser from '../lib/is-browser'
import i18n from '../config/i18n'
import {
  Language,
  LanguageCode,
  LANGUAGE_CODES,
  LANGUAGE_NAMES,
} from '../types/language'

const LOCAL_STORAGE_KEY = 'preferred_language'

/**
 * Looks up a language by its code and returns the corresponding language object.
 * Throws an error if the language code is not supported.
 *
 * @param {LanguageCode} code - The language code to look up
 * @returns {Language} The language object corresponding to the given code
 * @throws {Error} If the language code is not supported by the application
 */
function lookupLanguageByCode(code: LanguageCode): Language {
  const result = LANGUAGE_NAMES[code]
  if (!result) {
    throw new Error(
      `Language code "${code}" is not supported by the application`
    )
  }
  return { code, name: result }
}

type State = {
  language: Language
  supportedLanguages: Language[]
}

type Dispatch = (action: Action) => void

type Action =
  | { type: 'SET_LANGUAGE'; payload: Language }
  | { type: 'SET_SUPPORTED_LANGUAGES'; payload: LanguageCode[] }

type Context = {
  state: State
  dispatch: Dispatch
}

const LanguageContext = React.createContext<Context | undefined>(undefined)

/**
 * Reduces the state based on the action type.
 * Updates the language or supported languages in the state accordingly.
 *
 * @param {State} state - The current state
 * @param {Action} action - The action to perform on the state
 * @returns {State} The updated state
 */
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_LANGUAGE':
      return {
        ...state,
        language: action.payload,
      }
    case 'SET_SUPPORTED_LANGUAGES':
      return {
        ...state,
        supportedLanguages: action.payload.map(lookupLanguageByCode),
      }
    default:
      return state
  }
}

type ProviderProps = {
  children: React.ReactNode
  supportedLanguageCodes?: LanguageCode[]
}

/**
 * Provider component for managing language state throughout the application.
 * Uses React's Context API to make language preferences available to child components.
 *
 * Initializes with the preferred language from either:
 * - localStorage
 * - browser preferences
 * - or defaults to 'en'
 *
 * @param props.children - Child components that will have access to language context
 * @param props.supportedLanguageCodes - Optional array of supported language codes
 */
function LanguageProvider(props: ProviderProps) {
  const preferredLanguageCode = getPreferredLanguageCode()
  const initialState: State = {
    language: lookupLanguageByCode(preferredLanguageCode),
    supportedLanguages:
      props.supportedLanguageCodes?.map(lookupLanguageByCode) ||
      LANGUAGE_CODES.map(lookupLanguageByCode),
  }
  const [state, dispatch] = React.useReducer(reducer, initialState)

  // Update i18n when preferred language changes
  React.useEffect(() => {
    i18n.changeLanguage(preferredLanguageCode)
  }, [preferredLanguageCode])

  // Reassess the preferred language when the browser language changes
  React.useEffect(() => {
    if (!isBrowser()) {
      return
    }

    const handleLanguageChange = () => {
      const newLanguageCode = getPreferredLanguageCode()
      setLanguage(dispatch, newLanguageCode)
    }

    window.addEventListener('languagechange', handleLanguageChange)
    return () => {
      window.removeEventListener('languagechange', handleLanguageChange)
    }
  }, [])

  return (
    <LanguageContext.Provider value={{ state, dispatch }}>
      {props.children}
    </LanguageContext.Provider>
  )
}

/**
 * Gets the user's preferred language based on a priority order:
 * 1. Stored preference in localStorage (if available and supported)
 * 2. Browser's language setting (if supported)
 * 3. Falls back to default 'en' if no supported preferences found
 *
 * @returns The preferred language code
 */
function getPreferredLanguageCode(): LanguageCode {
  const defaultValue = 'en'

  if (!isBrowser()) {
    return defaultValue
  }
  // Check if the user has a preferred language stored in localStorage
  const localStorageValue = localStorage.getItem(LOCAL_STORAGE_KEY)
  if (localStorageValue && isSupported(localStorageValue)) {
    return localStorageValue as LanguageCode
  }
  // Check if the user's browser has a preferred language
  const browserPreferredValue = navigator.language.split('-')[0]
  if (isSupported(browserPreferredValue)) {
    return browserPreferredValue as LanguageCode
  }
  return defaultValue as LanguageCode
}

/**
 * Checks if a given language code is supported by the application.
 *
 * @param languageCode - The language code to check (e.g. 'en', 'es')
 * @returns True if the language code is supported, false otherwise
 * @example
 * isSupported('en') // Returns true
 * isSupported('xx') // Returns false
 */
function isSupported(languageCode: string): boolean {
  return LANGUAGE_CODES.includes(languageCode as LanguageCode)
}

/**
 * Hook to access the language context from within a component.
 * Must be used within a LanguageProvider component.
 *
 * @returns An object containing:
 *   - state: The current language state
 *   - dispatch: Function to update the language state
 * @throws If used outside of a LanguageProvider
 */
function useLanguage(): Context {
  const context = React.useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

/**
 * Updates the application's language setting.
 * Stores the selected language in localStorage, updates the language context state, and updates the i18n setting.
 *
 * @param dispatch - The dispatch function from the language context
 * @param languageCode - The language code to set as the new language (e.g. 'en', 'es')
 * @throws If the language code is not supported
 * @throws If localStorage is not available or writing fails
 * @example
 * setLanguage(languageContext.dispatch, 'es') // Sets language to Spanish
 */
function setLanguage(dispatch: Dispatch, languageCode: LanguageCode): void {
  try {
    const language = lookupLanguageByCode(languageCode)
    localStorage.setItem(LOCAL_STORAGE_KEY, language.code)
    i18n.changeLanguage(language.code)
    dispatch({ type: 'SET_LANGUAGE', payload: language })
  } catch (error) {
    console.error(error)
  }
}

/**
 * Sets the supported languages in the language context state.
 *
 * @param dispatch - The dispatch function from the language context
 * @param languages - The array of language codes to set as supported
 */
function setSupportedLanguages(
  dispatch: Dispatch,
  languages: LanguageCode[]
): void {
  dispatch({ type: 'SET_SUPPORTED_LANGUAGES', payload: languages })
}

export { LanguageProvider, setLanguage, setSupportedLanguages, useLanguage }
